import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import Languages from "lang";
import { LANGUAGES } from "util/Constant";
import { loadRemote } from "@module-federation/enhanced/runtime";

const resources = Languages;

// Store the initialized instance
let i18nInstance = null;

/**
 * Initialize i18n based on whether this is the host or remote
 * @param {boolean} isHost - If true, merges with host instance. If false, creates own i18n instance.
 * @returns {Promise<i18n>} The i18n instance
 */
async function initI18n(isHost = false) {
  if (isHost) {
    // Load the host i18n instance at runtime using loadRemote
    const hostModule = await loadRemote("host/i18n");
    const hostI18n = hostModule?.default;

    if (!hostI18n) {
      throw new Error("❌ Host i18n instance not found — host is required for translations");
    }

    // Get the default namespace from host i18n options, or use 'translation' as fallback
    const defaultNamespace =
      hostI18n.options?.defaultNS || hostI18n.options?.ns?.[0] || "translation";

    // Add remote resources to host i18n instance using addResourceBundle
    // Resources format: { en: { translation: {...} }, fr: { translation: {...} } }
    Object.keys(resources).forEach((lang) => {
      const langResources = resources[lang];

      // Handle namespace format: { translation: {...} }
      if (langResources?.translation) {
        // Add each namespace to the host i18n instance
        Object.keys(langResources).forEach((namespace) => {
          hostI18n.addResourceBundle(
            lang,
            namespace,
            langResources[namespace],
            true, // deep merge
            true // overwrite existing keys (remote takes precedence)
          );
        });
      } else if (langResources && typeof langResources === "object") {
        // Handle flat format: {...}
        hostI18n.addResourceBundle(
          lang,
          defaultNamespace,
          langResources,
          true, // deep merge
          true // overwrite existing keys (remote takes precedence)
        );
      }
    });

    i18nInstance = hostI18n;
    console.log("i18n", hostI18n);
    return hostI18n;
  } else {
    i18n.use(initReactI18next).init({
      resources,
      lng: window?.locale?.defaultLanguage || LANGUAGES.EN,
      interpolation: {
        escapeValue: false, // react already safes from xss
      },
    });
    i18nInstance = i18n;
    //console.log("i18n", i18n);
    return i18n;
  }
}

// Export the function for explicit initialization
export { initI18n };

// Export the instance (will be set after initI18n is called)
export default i18nInstance;
